import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { passwordSchema } from '@/utils/passwordValidation';
import { useTwoFactorAuth } from '@/hooks/useTwoFactorAuth';
import { TwoFactorVerification } from '@/components/TwoFactorVerification';

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Email inválido" }),
  password: z.string().min(6, { message: "Senha deve ter no mínimo 6 caracteres" })
});

const signupSchema = z.object({
  fullName: z.string().trim().min(3, { message: "Nome deve ter no mínimo 3 caracteres" }).max(100, { message: "Nome muito longo" }),
  email: z.string().trim().email({ message: "Email inválido" }),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { show2FA, userInfo, check2FARequired, initiate2FA, cancel2FA, complete2FA } = useTwoFactorAuth();
  
  // Detectar se deve abrir aba de cadastro via query string
  const searchParams = new URLSearchParams(location.search);
  const shouldShowSignup = searchParams.get('signup') === 'true';
  const defaultTab = shouldShowSignup ? 'signup' : 'login';
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [checking2FA, setChecking2FA] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Redirect if already logged in (mas não durante o processo de 2FA)
  useEffect(() => {
    if (user && !show2FA && !checking2FA) {
      navigate('/dashboard');
    }
  }, [user, navigate, show2FA, checking2FA]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);
    setChecking2FA(true); // CRÍTICO: Prevenir redirecionamento automático

    try {
      const validatedData = loginSchema.parse(loginData);
      
      // PASSO 1: Fazer login com credenciais
      const { data, error } = await supabase.auth.signInWithPassword({
        email: validatedData.email,
        password: validatedData.password,
      });

      if (error) {
        setChecking2FA(false);
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Email ou senha incorretos');
        } else {
          toast.error(error.message);
        }
        return;
      }

      if (!data.user) {
        setChecking2FA(false);
        toast.error('Erro ao fazer login');
        return;
      }

      console.log('✅ Login successful, checking 2FA requirement...');

      // PASSO 2: Verificar se 2FA está habilitado
      const requires2FA = await check2FARequired(data.user.id);
      
      if (requires2FA) {
        console.log('🔐 2FA required - showing verification screen (user remains logged in)');
        
        // Buscar nome do usuário
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', data.user.id)
          .single();
        
        // Mostrar tela de 2FA (usuário está logado, mas checking2FA impede navegação)
        await initiate2FA(
          data.user.id,
          data.user.email || '',
          profile?.full_name || 'Usuário',
          validatedData.password
        );
        
        console.log('✅ 2FA verification screen displayed, waiting for code...');
        // checking2FA permanece true até verificação do código
        return;
      }

      // PASSO 3: Se NÃO precisa de 2FA, liberar navegação
      console.log('✅ No 2FA required, navigating to dashboard');
      setChecking2FA(false);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      setChecking2FA(false);
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const validatedData = signupSchema.parse(signupData);
      const redirectUrl = `https://dashi.app.br/dashboard`;

      const { data, error } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: validatedData.fullName,
          }
        }
      });

      if (error) {
        if (error.message.includes('User already registered')) {
          toast.error('Este email já está cadastrado');
        } else {
          toast.error(error.message);
        }
        return;
      }

      // Enviar email de boas-vindas customizado via Resend
      if (data.user) {
        try {
          await supabase.functions.invoke('send-auth-email', {
            body: {
              email: validatedData.email,
              fullName: validatedData.fullName,
              type: 'welcome'
            }
          });
          console.log('✅ Email de boas-vindas enviado');
        } catch (emailError) {
          console.error('⚠️ Erro ao enviar email de boas-vindas:', emailError);
          // Não bloqueia o cadastro se o email falhar
        }
      }

      toast.success('Conta criada com sucesso! Você já pode fazer login.');
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const emailSchema = z.string().trim().email({ message: "Email inválido" });
      const validatedEmail = emailSchema.parse(resetEmail);

      const { error } = await supabase.functions.invoke('request-password-reset', {
        body: { email: validatedEmail }
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success('Email de recuperação enviado! Verifique sua caixa de entrada.');

      setShowResetPassword(false);
      setResetEmail('');
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Mostrar tela de verificação 2FA se necessário
  if (show2FA && userInfo) {
    console.log('Rendering 2FA verification screen');
    return (
      <TwoFactorVerification
        userId={userInfo.userId}
        email={userInfo.email}
        userName={userInfo.userName}
        password={userInfo.password}
        onSuccess={() => {
          complete2FA();
          setChecking2FA(false);
          toast.success('Login realizado com sucesso!');
          navigate('/dashboard');
        }}
        onCancel={() => {
          cancel2FA();
          setChecking2FA(false);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-primary">
            <TrendingUp className="h-8 w-8" />
            <span className="text-2xl font-bold">Dashi</span>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Bem-vindo</CardTitle>
            <CardDescription>Entre ou crie sua conta para continuar</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Cadastro</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {showResetPassword ? (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Enviando...' : 'Enviar email de recuperação'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => setShowResetPassword(false)}
                  >
                    Voltar para login
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Senha</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => setShowResetPassword(true)}
                    >
                      Esqueci minha senha
                    </Button>
                  </div>
                </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nome Completo</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="João Silva"
                    value={signupData.fullName}
                    onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                    required
                  />
                  {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    required
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    required
                  />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">Confirmar Senha</Label>
                  <Input
                    id="signup-confirm"
                    type="password"
                    placeholder="••••••"
                    value={signupData.confirmPassword}
                    onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                    required
                  />
                  {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                </div>
                
                {/* Requisitos de Senha */}
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">A senha deve conter:</p>
                  <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Mínimo de 6 caracteres
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Pelo menos 1 letra maiúscula
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Pelo menos 1 letra minúscula
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Pelo menos 1 número
                    </li>
                  </ul>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Cadastrando...' : 'Criar Conta'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          <div className="mt-6 flex justify-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
